import React from 'react'
import { FormattedMessage, MessageDescriptor } from 'react-intl'
import { WithTooltip } from '../../design-system'
import { formLabelCss } from '../text'
import { CsFilterIcon } from '../icons'
import { css, cx } from '@emotion/css'
import { theme } from '@contrast-security-inc/design-system-foundations'
import {
  ColumnHeaderFilters,
  ColumnHeaderProps,
  OnGroupToggleFn,
  OnOptionToggleFn,
  OnMultiGroupToggleFn
} from '.'
import { Filters } from '../filter'
import { GridHeaderBetaBadge } from '../../components/badge/grid-header-beta-badge'
import { FilterConfigs, FilterSelections } from '../filter/filters'
import { Link } from '@contrast-security-inc/design-system-typography'

const filterIconCommonCss = css`
  flex: none;
  margin-left: 5px;
  font-size: 12px;
  line-height: 1em;
`

const containerCss = css`
  display: flex;
  align-items: center;
  padding: 8px;
`

const clickableCss = css`
  cursor: pointer;
`

const clickableContainerCss = cx(containerCss, clickableCss)

const clearFilterLinkCss = css`
  font-size: 12px;
  margin: 2px 0px 0px 5px;
  display: flex;
  align-items: center;
`

export function ClearFilterContent({
  onClick,
  className
}: {
  readonly onClick: () => void
  readonly className?: string
}) {
  return (
    <FormattedMessage id="title_clear">
      {(text) => (
        <div
          onMouseDown={(e: React.SyntheticEvent<HTMLElement>) => {
            // prevent opening filter popper when clicking clear
            e.stopPropagation()
          }}
        >
          <Link
            className={cx(clearFilterLinkCss, className)}
            onClick={(e: React.SyntheticEvent<HTMLElement>) => {
              e.stopPropagation()
              onClick()
            }}
            children={text}
          />
        </div>
      )}
    </FormattedMessage>
  )
}

interface FilterContentProps {
  readonly isFiltered: boolean
  readonly isFilterable: boolean
  readonly onClear?: () => void
  readonly message?: MessageDescriptor
}

function FilterContent({
  isFiltered,
  isFilterable,
  onClear,
  message
}: FilterContentProps) {
  const filterIconColor = isFiltered
    ? theme.color.turquoise70
    : theme.color.neutral100

  const filterIconCss = cx(
    filterIconCommonCss,
    css`
      color: ${filterIconColor};
    `
  )

  return (
    <React.Fragment>
      <WithTooltip
        tiny
        disabled={!isFilterable}
        placement="top"
        renderTooltipContent={() => (
          <FormattedMessage id="action_applyFilters" />
        )}
        children={({ ref, onMouseEnter, onMouseLeave }) => (
          <span
            ref={ref}
            className={clickableCss}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          >
            <div
              data-e2e={`filter-icon-${message ? message.id : ''}`}
              className={filterIconCss}
            >
              <CsFilterIcon />
            </div>
          </span>
        )}
      />
      {isFiltered && onClear && <ClearFilterContent onClick={onClear} />}
    </React.Fragment>
  )
}

interface ColumnHeaderContentProps {
  readonly isClickable: boolean
  readonly message?: MessageDescriptor
  readonly messageClassName?: string
  readonly onClear?: () => void
  readonly isFilterable: boolean
  readonly isFiltered: boolean
  readonly isBeta?: boolean
}

function ColumnHeaderContent({
  isClickable,
  isFiltered,
  isFilterable,
  message,
  messageClassName,
  onClear,
  isBeta
}: ColumnHeaderContentProps) {
  const columnHeaderCss = isClickable ? clickableContainerCss : containerCss
  const messageCss = cx(formLabelCss, messageClassName)
  return (
    <div
      className={columnHeaderCss}
      data-e2e={`column-header-${message ? message.id : ''}`}
      id={`column-header-${message ? message.id : ''}`}
    >
      {message && (
        <FormattedMessage {...message}>
          {(text) => <div className={messageCss} children={text} />}
        </FormattedMessage>
      )}
      {isBeta && <GridHeaderBetaBadge />}
      {isFilterable && (
        <FilterContent
          message={message}
          isFiltered={isFiltered}
          isFilterable={isFilterable}
          onClear={onClear}
        />
      )}
    </div>
  )
}

export function doFiltersHaveOptions(
  groupConfigs: FilterConfigs,
  groupIDs: string[]
) {
  for (const groupID of groupIDs) {
    const config = groupConfigs[groupID]
    if (config && config.options && config.options.length > 0) {
      return true
    }
  }
  return false
}

export function doFiltersHaveSelectionsOnGroups(
  selections: FilterSelections,
  groupIDs: string[]
): boolean {
  let hasSelections = false

  Object.keys(selections).forEach((key) => {
    if (
      Array.isArray(groupIDs) &&
      groupIDs.find((id) => id === key) && // verify if the selection belongs to the available groups for the current filters
      Array.isArray(selections[key]) &&
      selections[key].length > 0
    ) {
      hasSelections = true
    }
  })
  return hasSelections
}

export interface ValidFilters<TAggregationCategory extends string>
  extends ColumnHeaderFilters<TAggregationCategory> {
  readonly onMultiGroupToggle: OnMultiGroupToggleFn<TAggregationCategory>
  readonly onGroupToggle: OnGroupToggleFn<TAggregationCategory>
  readonly onOptionToggle: OnOptionToggleFn<TAggregationCategory>
}

/**
 * This is "user-defined type guard" - https://www.typescriptlang.org/docs/handbook/advanced-types.html#user-defined-type-guards
 * This is used to determine if we should render the filters or not (in the column header)
 */
export function doShowFilters<TAggregationCategory extends string>(
  filters:
    | ColumnHeaderFilters<TAggregationCategory>
    | ValidFilters<TAggregationCategory>
    | undefined
): filters is ValidFilters<TAggregationCategory> {
  return (
    !!filters &&
    !!filters.selections &&
    !!filters.onGroupToggle &&
    !!filters.onOptionToggle &&
    (doFiltersHaveOptions(filters.groupConfigs, filters.groupIDs) ||
      doFiltersHaveSelectionsOnGroups(filters.selections, filters.groupIDs))
  )
}

export function ColumnHeader<TAggregationCategory extends string>({
  message,
  messageClassName,
  filters,
  clearAll,
  render,
  isDrilldown,
  isBeta
}: ColumnHeaderProps<TAggregationCategory> & {
  readonly isDrilldown?: boolean
}) {
  if (render) {
    return render({
      filters,
      clearAll,
      renderContent: ({ isClickable, onClear, isFilterable, isFiltered }) => {
        return (
          <ColumnHeaderContent
            message={message}
            messageClassName={messageClassName}
            isClickable={isClickable}
            onClear={onClear}
            isFilterable={isFilterable}
            isFiltered={isFiltered}
            isBeta={isBeta}
          />
        )
      }
    })
  }

  return doShowFilters<TAggregationCategory>(filters) ? (
    <Filters
      isDrilldown={isDrilldown}
      groupConfigs={filters.groupConfigs}
      selections={filters.selections}
      groupIDs={filters.groupIDs}
      onGroupToggle={filters.onGroupToggle}
      onMultiGroupToggle={filters.onMultiGroupToggle}
      onOptionToggle={filters.onOptionToggle}
      children={({ isTransitioning }) => (
        <ColumnHeaderContent
          message={message}
          messageClassName={messageClassName}
          isClickable={!isTransitioning}
          onClear={clearAll}
          isFiltered={areSomeFiltersActive(filters)}
          isFilterable
          isBeta={isBeta}
        />
      )}
    />
  ) : (
    <ColumnHeaderContent
      message={message}
      messageClassName={messageClassName}
      isFilterable={false}
      isFiltered={false}
      isClickable={false}
      isBeta={isBeta}
    />
  )
}

export function areSomeFiltersActive<TAggregationCategory extends string>(
  filters: ValidFilters<TAggregationCategory>
) {
  return (
    filters &&
    filters.groupIDs &&
    filters.groupIDs.some((groupID) =>
      filters.selections &&
      filters.selections[groupID] &&
      filters.selections[groupID].length > 0
        ? true
        : false
    )
  )
}
